import { isMissingUnits } from './catalog-constants';
import { UnitValue } from './types';
import { isCompositeDisplayUnit, isRawGaugePsiKey, toNumber } from './utils';

/**
 * True when psig-inhg composite display should govern both transform suppression
 * and formatter selection for this source. Real decoded units (e.g. inHg) keep their
 * own formatter and may still request other transform targets.
 */
export function compositeDisplayAppliesTo(sourceKey: string, displayUnits?: string): boolean {
  if (!isCompositeDisplayUnit(displayUnits)) {
    return false;
  }

  const key = sourceKey.toLowerCase();
  return isMissingUnits(key) || isRawGaugePsiKey(key) || isCompositeDisplayUnit(key);
}

/**
 * Resolves which unit `formatUnits` should pass to `transformUnits`. Display-only
 * conventions (vacuum → inHg for negative gauge sources) live here — not in
 * formatters and not in write-back/chart `transformUnits` callers.
 */
export function resolveFormatTransformTarget(
  normalized: UnitValue,
  displayUnits?: string
): string | undefined {
  const sourceKey = (normalized.units ?? '').toLowerCase();

  // Wire-encoded psig-inhg (or missing units): pass displayUnits through so the
  // transformer knows whether to decode, preserve composite encoding, or convert.
  if (isMissingUnits(sourceKey) || isCompositeDisplayUnit(sourceKey)) {
    return displayUnits;
  }

  // Composite display on gauge/missing/composite sources: skip transform —
  // formatPsigInhg reads sign/magnitude on the raw value. undefined = no conversion.
  if (compositeDisplayAppliesTo(sourceKey, displayUnits)) {
    return undefined;
  }

  if (isRawGaugePsiKey(sourceKey)) {
    const numeric = toNumber(normalized.value);
    const displayKey = displayUnits?.toLowerCase();
    const isExplicitRealTarget =
      displayUnits != null && displayKey !== 'psi' && displayKey !== 'psig';

    if (numeric != null && numeric < 0 && !isExplicitRealTarget) {
      return 'inHg';
    }

    return displayUnits;
  }

  return displayUnits;
}

/**
 * Selects formatPsigInhg when composite display applies to the source.
 */
export function resolveCompositeDisplayFormatterKey(
  sourceUnits: string,
  transformTarget?: string
): string | undefined {
  if (!compositeDisplayAppliesTo(sourceUnits, transformTarget)) {
    return undefined;
  }

  return transformTarget!.toLowerCase();
}
